import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { searchFeedback } from '../store/AdminSlices/feedbackSlice';
import { CardSkeleton } from '../components/Skeleton';

interface Review {
  id: string;
  reviewerName: string;
  reviewerAvatar: string;
  revieweeName: string;
  rating: number;
  comment: string;
  reviewedDate: string;
  type: 'student-review' | 'coach-review';
}

const ReviewsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { feedbacks, loading, error } = useAppSelector((state) => state.feedback);
  const [activeTab, setActiveTab] = useState<'student-review' | 'coach-review'>('student-review');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch feedback on component mount and when tab changes
  useEffect(() => {
    const role = activeTab === 'student-review' ? 'coach' : 'user';
    dispatch(searchFeedback({ role, search: searchQuery }));
  }, [dispatch, activeTab, searchQuery]);

  // Search when query changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      const role = activeTab === 'student-review' ? 'coach' : 'user';
      dispatch(searchFeedback({ role, search: searchQuery }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, dispatch, activeTab]);

  // Convert API feedback to Review format
  const convertFeedbackToReviews = (): Review[] => {
    if (!feedbacks || feedbacks.length === 0) {
      return []; // Return empty array if no API data
    }

    return feedbacks.map((feedback) => ({
      id: feedback.id,
      reviewerName: feedback.reviewer_name || 'Unknown Reviewer',
      reviewerAvatar: feedback.reviewer_avatar || '',
      revieweeName: feedback.user_name || 'Unknown User',
      rating: feedback.rating,
      comment: feedback.text,
      reviewedDate: new Date(feedback.created_at).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
      type: activeTab,
    }));
  };

  const reviews = convertFeedbackToReviews();

  const filteredReviews = reviews.filter((review) => {
    const matchesTab = review.type === activeTab;
    const matchesSearch =
      !searchQuery ||
      review.reviewerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      review.revieweeName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const studentReviewCount = feedbacks.filter((f: any) => activeTab === 'student-review').length || reviews.filter((r) => r.type === 'student-review').length;
  const coachReviewCount = feedbacks.filter((f: any) => activeTab === 'coach-review').length || reviews.filter((r) => r.type === 'coach-review').length;

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <svg key={i} className="h-5 w-5 fill-orange-400" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      } else {
        stars.push(
          <svg key={i} className="h-5 w-5 fill-gray-300" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      }
    }
    
    return stars;
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-3">
        <button
          onClick={() => setActiveTab('student-review')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'student-review'
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Student Reviews of Coaches ({studentReviewCount})
        </button>
        <button
          onClick={() => setActiveTab('coach-review')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            activeTab === 'coach-review'
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Coach Reviews of Students ({coachReviewCount})
        </button>
      </div>

      {/* Search */}
      <div className="relative w-80">
        <input
          type="text"
          placeholder="Search Coach"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-300 focus:outline-none focus:ring-0"
        />
        <svg
          className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Reviews List */}
      {!loading && !error && (
        <div className="space-y-4">
        {filteredReviews.map((review, index) => (
          <div
            key={review.id}
            className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]"
          >
            {/* Review Header */}
            <div className="mb-4 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {review.reviewerAvatar ? (
                    <img 
                      src={review.reviewerAvatar} 
                      alt={review.reviewerName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-medium text-white">
                      {review.reviewerName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{review.reviewerName}</h3>
                  <p className="text-sm text-gray-500">reviewed {review.revieweeName}</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 whitespace-nowrap">Reviewed: {review.reviewedDate}</p>
            </div>

            {/* Rating */}
            <div className="mb-3 flex items-center gap-2">
              <div className="flex gap-0.5">
                {renderStars(review.rating)}
              </div>
              <span className="text-sm font-semibold text-gray-900">{review.rating.toFixed(1)}</span>
            </div>

            {/* Review Comment */}
            <p className="text-sm leading-relaxed text-gray-900">{review.comment}</p>
          </div>
        ))}
        </div>
      )}

      {!loading && !error && filteredReviews.length === 0 && (
        <div className="rounded-xl border border-[#F2F4F7] bg-white py-12 text-center shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
          <p className="text-sm text-gray-400">No reviews found</p>
        </div>
      )}
    </div>
  );
};

export default ReviewsPage;
